import React from 'react';
import {
  Box,
  Paper,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Cancel as CancelIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/dateFormat';

import { txLabels, formatPaymentAmount } from '../../utils/checkTransactionsDisplay';
/**
 * @param {object} props
 * @param {Array} props.transactions
 * @param {boolean} props.isMobile
 * @param {boolean} props.isManager
 * @param {function} [props.onCancelPayment]
 * @param {function} [props.isActionPending]
 * @param {string} props.emptyMessage
 * @param {'inTable'|'standalone'} [props.desktopEmptyMode='inTable']
 * @param {object} [props.emptyStandaloneSx]
 */
function CheckTransactionsTable({
  transactions,
  paymentCurrency,
  isMobile,
  isManager,
  onCancelPayment,
  isActionPending,
  emptyMessage,
  desktopEmptyMode = 'inTable',
  emptyStandaloneSx = {},
}) {
  const { t } = useTranslation();
  const cancelTooltip = t('cashiering.cancelPayment');
  const dateHeader = t('cashiering.date');
  const typeHeader = t('cashiering.type');
  const amountHeader = t('cashiering.amount');
  const noteHeader = t('common.note');
  const performedHeader = t('cashiering.performedBy');
  const actionsHeader = t('cashiering.actions');
  const emptyDash = t('common.emptyValue');

  const showCancel = Boolean(onCancelPayment) && isManager;

  if (!isMobile && desktopEmptyMode === 'standalone' && (!transactions || transactions.length === 0)) {
    return (
      <Typography color="text.secondary" textAlign="center" sx={{ py: 2, ...emptyStandaloneSx }}>{emptyMessage}</Typography>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(!transactions || transactions.length === 0) ? (
          <Typography color="text.secondary" variant="body2" textAlign="center" sx={{ py: 2 }}>{emptyMessage}</Typography>
        ) : (transactions || []).map((tx) => {
          const { isCancelled, typeLabel, typeColor, amountColor } = txLabels(tx, t);
          const useCashieringMobileCard = true;
          if (useCashieringMobileCard) {
            return (
              <Paper key={tx._id} variant="outlined" sx={{ p: 1.5, opacity: isCancelled ? 0.5 : 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box display="flex" gap={1} alignItems="center" mb={0.5}>
                      <Chip label={typeLabel} color={typeColor} size="small" variant="outlined" />
                      <Typography variant="body2" color={amountColor} fontWeight="bold">{formatPaymentAmount(tx, paymentCurrency)}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">{tx.createdAt ? formatDateTime(tx.createdAt) : emptyDash}</Typography>
                    {tx.note && <Typography variant="caption" color="text.secondary">{tx.note}</Typography>}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {tx.performedBy ? `${tx.performedBy.firstName || ''} ${tx.performedBy.lastName || ''}`.trim() || tx.performedBy.email : emptyDash}
                    </Typography>
                  </Box>
                  {showCancel && !isCancelled && (tx.type === 'deposit' || tx.type === 'payment') && (
                    <Tooltip title={cancelTooltip}>
                      <IconButton size="small" color="error" onClick={() => onCancelPayment(tx._id)}
                        disabled={isActionPending?.(`cancel-payment-${tx._id}`)} sx={{ border: '1px solid', borderColor: 'error.main', flexShrink: 0 }}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            );
          }
          return (
            <Card key={tx._id} variant="outlined" sx={isCancelled ? { opacity: 0.5 } : {}}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip label={typeLabel} color={typeColor} size="small" variant="outlined" />
                  <Typography variant="body1" color={amountColor} fontWeight="bold">
                    {formatPaymentAmount(tx, paymentCurrency)}
                  </Typography>
                  {showCancel && !isCancelled && (tx.type === 'deposit' || tx.type === 'payment') && (
                    <Tooltip title={cancelTooltip}>
                      <IconButton size="small" color="error" onClick={() => onCancelPayment(tx._id)}
                        disabled={isActionPending?.(`cancel-payment-${tx._id}`)}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {tx.note && <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{tx.note}</Typography>}
                <Typography variant="caption" color="text.secondary">
                  {tx.createdAt ? formatDateTime(tx.createdAt) : emptyDash}
                  {tx.performedBy && ` · ${`${tx.performedBy.firstName || ''} ${tx.performedBy.lastName || ''}`.trim() || tx.performedBy.email}`}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{dateHeader}</TableCell>
            <TableCell>{typeHeader}</TableCell>
            <TableCell>{amountHeader}</TableCell>
            <TableCell>{noteHeader}</TableCell>
            <TableCell>{performedHeader}</TableCell>
            {showCancel && <TableCell align="right">{actionsHeader}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {(transactions || []).map((tx) => {
            const { isCancelled, typeLabel, typeColor, amountColor } = txLabels(tx, t);
            return (
              <TableRow key={tx._id} sx={isCancelled ? { opacity: 0.5, textDecoration: 'line-through' } : {}}>
                <TableCell><Typography variant="caption">{tx.createdAt ? formatDateTime(tx.createdAt) : emptyDash}</Typography></TableCell>
                <TableCell><Chip label={typeLabel} color={typeColor} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Typography variant="body2" color={amountColor} fontWeight="bold">{formatPaymentAmount(tx, paymentCurrency)}</Typography>
                </TableCell>
                <TableCell>{tx.note || emptyDash}</TableCell>
                <TableCell>
                  {tx.performedBy ? `${tx.performedBy.firstName || ''} ${tx.performedBy.lastName || ''}`.trim() || tx.performedBy.email : emptyDash}
                </TableCell>
                {showCancel && (
                  <TableCell align="right">
                    {!isCancelled && (tx.type === 'deposit' || tx.type === 'payment') && (
                      <Tooltip title={cancelTooltip}>
                        <IconButton size="small" color="error" onClick={() => onCancelPayment(tx._id)}
                        disabled={isActionPending?.(`cancel-payment-${tx._id}`)} sx={{ border: '1px solid', borderColor: 'error.main' }}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {(!transactions || transactions.length === 0) && (
            <TableRow>
              <TableCell colSpan={showCancel ? 6 : 5} align="center" sx={{ py: 2 }}>
                <Typography color="text.secondary" variant="body2">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CheckTransactionsTable;
